import mixpanel from "mixpanel-browser";
import { getConsent } from "../lib/storage";

export type SearchSuggestionType = "place" | "category" | "brand" | "coordinates";
export type SearchSuggestionSource = "mapbox" | "noma";

export interface InstrumentationEvents {
  // Auth events
  "Sign In Page Viewed": void;
  "Magic Link Verified": void;

  // Map events
  "Map Editor Opened": { mapId: string; placeCount: number; daysSinceCreation: number };
  "Adventure Map Viewed": { mapId: string; placeCount: number };
  "Landing Page Viewed": { slug: string; placeCount: number };

  // Place events
  "Place Selected":
    | { map: string; placeId: string; placeType: string; context: "adventure_map" }
    | { landingPage: string; placeId: string; placeType: string; context: "landing_page" };

  // Search events
  "Search Suggestion Selected": {
    suggestionType: SearchSuggestionType;
    suggestionSource: SearchSuggestionSource;
  };

  // Sharing events
  "Landing Page Builder Opened": { mapId: string; isAlreadyPublished: boolean };

  // Engagement events
  "External Link Clicked": {
    placeId: string;
    linkType: "phone" | "email" | "link";
    context: "adventure_map" | "landing_page";
  };
}

export interface Instrumentation {
  /** Enable tracking. Call when user accepts cookies. */
  optIn: () => void;
  /** Disable tracking. Call when user rejects cookies. */
  optOut: () => void;
  /** Track a typed event with optional properties. */
  track: <T extends keyof InstrumentationEvents>(
    event: T,
    ...args: InstrumentationEvents[T] extends void ? [] : [properties: InstrumentationEvents[T]]
  ) => void;
  /** Track a page view. */
  viewPage: () => void;
  /** Identify the current user by ID. */
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  /** Reset user identity (call on sign out). */
  reset: () => void;
}

const instrumentation: Instrumentation = { optIn, optOut, track, viewPage, identify, reset };

/**
 * Returns the instrumentation API for tracking events.
 */
export function useInstrumentation(): Instrumentation {
  return instrumentation;
}

function optIn(): void {
  if (mixpanel.has_opted_in_tracking()) return;
  log("opt_in_tracking");
  mixpanel.opt_in_tracking();
}

function optOut(): void {
  if (mixpanel.has_opted_out_tracking()) return;
  log("opt_out_tracking");
  mixpanel.opt_out_tracking();
}

function track<T extends keyof InstrumentationEvents>(
  event: T,
  ...args: InstrumentationEvents[T] extends void ? [] : [properties: InstrumentationEvents[T]]
): void {
  const properties = args[0] ?? {};
  log("track:", event, properties);
  mixpanel.track(event, properties);
}

function viewPage(): void {
  log("track_pageview:", window.location.pathname + window.location.search);
  mixpanel.track_pageview();
}

function identify(userId: string, traits?: Record<string, unknown>): void {
  log("identify:", userId);
  mixpanel.identify(userId);
  if (traits) mixpanel.people.set(traits);
}

function reset(): void {
  log("reset");
  mixpanel.reset();
}

let initialized = false;
const isDev = import.meta.env.DEV;

function log(...args: unknown[]) {
  if (isDev) console.log("[instrumentation]", ...args);
}

function getToken(): string {
  if (typeof window !== "undefined" && window.INSTRUMENTATION_CONFIG?.mixpanel) {
    return window.INSTRUMENTATION_CONFIG.mixpanel.token;
  }
  return import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN || "";
}

export function initInstrumentation() {
  if (initialized) {
    log("already initialized");
    return;
  }

  const token = getToken();

  if (!token) {
    log("disabled (no token)");
    return;
  }

  const priorConsent = getConsent();
  const optedOut = priorConsent !== "accepted";

  log("initializing with token:", token.slice(0, 8) + "...", { priorConsent });

  mixpanel.init(token, {
    api_host: window.location.origin,
    api_routes: {
      track: "instrumentation/e/",
      engage: "instrumentation/p/",
      groups: "instrumentation/g/",
    },
    persistence: "localStorage",
    track_pageview: false,
    opt_out_tracking_by_default: optedOut,
    debug: isDev,
  });

  initialized = true;
  log("initialized", { optedOut });
}
